// Stato della pratica di SOSTITUZIONE di un ordine, in un posto solo.
//
// Lo leggono tre punti della pagina ordine (striscia promemoria, card in
// colonna 1, sezione resi): scritto tre volte diventerebbe tre risposte
// diverse alla stessa domanda — «va a un altro indirizzo?» e «da quanto è
// aperta?» — la prima volta che qualcuno tocca una delle tre copie.

import type { Order, ReplacementAddress } from "./types";

/** Da quanti giorni aperta la pratica diventa un promemoria ROSSO. */
export const GIORNI_PROMEMORIA_SOSTITUZIONE = 5;

export interface StatoSostituzione {
  /** Esiste un indirizzo di sostituzione registrato sull'ordine. */
  presente: boolean;
  /** L'indirizzo di sostituzione DIFFERISCE da quello di spedizione: il pezzo
   *  nuovo va da un'altra parte, e chi prepara la LDV deve accorgersene. */
  diverso: boolean;
  /** Giorni interi da quando la richiesta è stata aperta. `null` quando non
   *  c'è una data: nessun conteggio inventato. */
  giorniAperta: number | null;
  /** `giorniAperta >= GIORNI_PROMEMORIA_SOSTITUZIONE`. */
  inRitardo: boolean;
}

/** Minuscolo e spazi collassati: "  via   MAZZINI 14 " → "via mazzini 14".
 *  Davide riscrive l'indirizzo a mano mentre è al telefono — la stessa via
 *  battuta con due spazi non è un indirizzo diverso. */
function norm(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** La sola città dalla spedizione. `Order.customer.city` è il campo JOINATO
 *  dal mapper ("40121 Bologna BO"): si toglie ciò che il mapper ci ha messo
 *  dentro — CAP e provincia — invece di indovinare con una regex. */
function cittaSpedizione(customer: Order["customer"]): string {
  const scarta = new Set([norm(customer.postcode), norm(customer.province)].filter(Boolean));
  return norm(customer.city)
    .split(" ")
    .filter((token) => !scarta.has(token))
    .join(" ");
}

export function statoSostituzione(order: Order, now: Date = new Date()): StatoSostituzione {
  const indirizzo = order.replacementAddress ?? null;
  if (!indirizzo) {
    return { presente: false, diverso: false, giorniAperta: null, inRitardo: false };
  }

  const spedizione: Partial<Record<keyof ReplacementAddress | "destinatario", string>> = {
    address: order.customer.address,
    city: cittaSpedizione(order.customer),
    postcode: order.customer.postcode,
    state_iso: order.customer.province,
    country_iso: order.customer.country,
    destinatario: order.customer.name,
  };
  const sostituzione: typeof spedizione = {
    address: indirizzo.address,
    city: indirizzo.city,
    postcode: indirizzo.postcode,
    state_iso: indirizzo.state_iso,
    country_iso: indirizzo.country_iso,
    destinatario: `${indirizzo.name} ${indirizzo.surname}`,
  };

  // Un campo lasciato VUOTO è un «non lo so», non una differenza: l'indirizzo
  // si registra al telefono e si completa dopo. Confrontarlo con la
  // spedizione piena griderebbe «diverso» su ogni pratica appena aperta.
  const diverso = (Object.keys(sostituzione) as Array<keyof typeof sostituzione>).some((campo) => {
    const proposto = norm(sostituzione[campo]);
    return proposto !== "" && proposto !== norm(spedizione[campo]);
  });

  const apertaIl = order.replacementSetAt ? new Date(order.replacementSetAt) : null;
  const giorniAperta =
    apertaIl && !Number.isNaN(apertaIl.getTime())
      ? Math.max(0, Math.floor((now.getTime() - apertaIl.getTime()) / 86_400_000))
      : null;

  return {
    presente: true,
    diverso,
    giorniAperta,
    inRitardo: giorniAperta !== null && giorniAperta >= GIORNI_PROMEMORIA_SOSTITUZIONE,
  };
}

/** Giro di ritiro del RICAMBIO aperto (`raw.ritiro_sostituzione`, backend
 *  `ritiro_ricambio.py`): l'originale e' gia' partito e la merce pronta e'
 *  quella del pezzo nuovo. Da qui «Invia LDV» allega l'etichetta della
 *  sostituzione, e la card non deve dire solo «Spedito» (2001747332-A, 05/10). */
export function ritiroDelRicambio(order: Pick<Order, "raw">): boolean {
  return order.raw?.ritiro_sostituzione === true;
}
